// Stage 4.7 (2026-05-24) — de-facto treatment of RU/BY/UA passport holders
// per destination country, read from /data/passport_treatment.json.
//
// Scope reminder: this is NOT about visa or migration paperwork. It's about
// what happens after the user is in the country / tries to live there —
// banking access, real-estate, document issuance, public services, social
// climate. We split RU/BY/UA because host-country treatment of the three
// since 2022 is very different.

import { useQuery } from '@tanstack/react-query';
import type { Passport } from './types';

export type TreatmentStatus = 'welcoming' | 'cautious' | 'restrictive' | 'hostile';

interface CountryTreatment {
  ru: TreatmentStatus;
  by: TreatmentStatus;
  ua: TreatmentStatus;
  /** Legacy English evidence string (schema v1). Kept as fallback so old
   *  cached payloads still render; new lookups prefer evidence_en. */
  evidence?: string;
  /** Schema v2: per-language evidence so the notice renders in user lang. */
  evidence_en?: string;
  evidence_ru?: string;
}

interface PassportTreatmentFile {
  _meta: {
    generated: string;
    last_reviewed: string;
    next_review_due: string;
    schema_version: number;
    scale: TreatmentStatus[];
    scope: string;
    sources: string[];
    policy: Record<TreatmentStatus, string>;
    conservative_bias: string;
    legacy_cis_mapping: string;
  };
  countries: Record<string, CountryTreatment>;
}

export interface ResolvedTreatment {
  status: TreatmentStatus;
  evidence: string;
}

/**
 * Look up de-facto treatment of `passport` in `countryCode`. Returns null for
 * passports we don't restrict (EU/anglo/other) — saves callers a `passport in
 * ['ru','by','ua']` check. Returns null when the country isn't in the dataset
 * (treated as no-data, no penalty applied).
 *
 * `lang` picks the evidence translation; falls back to evidence_en → legacy
 * `evidence` field so older cached datasets continue to render.
 */
export function resolveTreatment(
  passport: Passport | undefined,
  countryCode: string,
  file: PassportTreatmentFile | undefined,
  lang: 'en' | 'ru' = 'en',
): ResolvedTreatment | null {
  if (!file) return null;
  if (passport !== 'ru' && passport !== 'by' && passport !== 'ua') return null;
  const entry = file.countries[countryCode.toUpperCase()];
  if (!entry) return null;
  const evidence =
    (lang === 'ru' ? entry.evidence_ru : entry.evidence_en) ??
    entry.evidence_en ??
    entry.evidence ??
    '';
  return { status: entry[passport], evidence };
}

const QUERY_KEY = ['passport-treatment'] as const;

export function usePassportTreatment() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<PassportTreatmentFile> => {
      const res = await fetch('/data/passport_treatment.json');
      if (!res.ok) throw new Error(`passport_treatment.json: ${res.status}`);
      return res.json();
    },
    staleTime: 24 * 60 * 60 * 1000, // 24h — dataset is curated, reviewed monthly
    gcTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * Numeric weight for sorting / soft penalty application. Higher = worse for
 * the user. Used by scoring to translate a categorical status into a score
 * multiplier without sprinkling magic numbers across the codebase.
 */
export function treatmentScoreMultiplier(status: TreatmentStatus): number {
  switch (status) {
    case 'welcoming':
      return 1.0;
    case 'cautious':
      return 0.95; // -5% nudge so two otherwise-equal countries rank welcoming above cautious
    case 'restrictive':
      return 0.7; // -30% as documented in the dataset's policy block
    case 'hostile':
      return 0; // hard exclusion — caller should also flip the `excluded` flag
  }
}

export function isTreatmentExcluding(status: TreatmentStatus): boolean {
  return status === 'hostile';
}
